/**
 * Toast Container Component
 * Displays toast notifications with animations
 *
 * @module components/ToastContainer
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Alert,
  AlertTitle,
  IconButton,
  Snackbar,
  Slide,
} from "@mui/material";
import {
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import { ENV } from "config/environment";

const TOAST_EVENT = "app:toast";

const ToastContainer = () => {
  const [toasts, setToasts] = useState([]);

  // Listen for toast events
  useEffect(() => {
    const handleToast = (event) => {
      const { message, type = "info", duration = ENV.CONFIG.ui.toastDuration } =
        event.detail;

      const id = Date.now() + Math.random();

      setToasts((prev) => [
        ...prev,
        {
          id,
          message,
          type,
          duration,
          createdAt: Date.now(),
        },
      ]);

      // Auto-remove after duration
      setTimeout(() => {
        removeToast(id);
      }, duration);
    };

    window.addEventListener(TOAST_EVENT, handleToast);

    return () => {
      window.removeEventListener(TOAST_EVENT, handleToast);
    };
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case "success":
        return <SuccessIcon />;
      case "error":
        return <ErrorIcon />;
      case "warning":
        return <WarningIcon />;
      case "info":
      default:
        return <InfoIcon />;
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: 80,
        right: 20,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        maxWidth: { xs: "calc(100% - 40px)", sm: 400 },
      }}
    >
      <AnimatePresence>
        {toasts.map((toast) => (
          <motion.div
            key={toast.id}
            initial={{ opacity: 0, y: -50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, x: 100, scale: 0.9 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
          >
            <Alert
              severity={toast.type}
              icon={getIcon(toast.type)}
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => removeToast(toast.id)}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
              sx={{
                width: "100%",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                backdropFilter: "blur(10px)",
                "& .MuiAlert-icon": {
                  alignItems: "center",
                },
              }}
            >
              {toast.message}
            </Alert>
          </motion.div>
        ))}
      </AnimatePresence>
    </Box>
  );
};

export default ToastContainer;

